import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Download,
  Zap,
  CheckCircle2,
  AlertCircle,
  Clock,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * FloatingProcessingIndicator
 * Card flutuante que mostra processamento em tempo real
 * Fica visível independente de qual aba o usuário está
 */
const FloatingProcessingIndicator = ({ 
  segmentId, 
  token, 
  isYouTubeUrl,
  processingArtifacts,
  onDismiss
}) => {
  const [status, setStatus] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);

  // Se não há artefatos sendo processados, não mostra nada
  const isProcessing = processingArtifacts && processingArtifacts.length > 0;

  // Iniciar polling quando começar a processar
  useEffect(() => {
    if (isYouTubeUrl && segmentId && token && isProcessing) {
      setStartTime(Date.now());
      checkStatus();
      setPollingActive(true);
    } else {
      setPollingActive(false);
      setElapsedTime(0);
    }
  }, [segmentId, isYouTubeUrl, token, isProcessing]);

  // Polling para atualizar status
  useEffect(() => {
    if (!pollingActive || !segmentId || !token) return;

    const pollInterval = setInterval(() => {
      checkStatus();
    }, 1000);

    return () => clearInterval(pollInterval);
  }, [pollingActive, segmentId, token]);

  // Atualizar tempo decorrido
  useEffect(() => {
    if (!isProcessing || !startTime) return;

    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, [isProcessing, startTime]);

  const checkStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/youtube/${segmentId}/status`,
        { headers }
      );

      setStatus(response.data);
    } catch (error) {
      console.debug('Status check:', error.message);
    }
  };

  if (!isYouTubeUrl || !isProcessing) {
    return null;
  }

  // Mostrar card mesmo sem status ainda (durante inicialização)
  const getProgressPercent = () => {
    if (!status?.progress) return 0;
    return status.progress.percent || 0;
  };

  const getStatusMessage = () => {
    if (!status) return 'Iniciando processamento...';
    
    switch (status.status) {
      case 'DOWNLOADING':
        return 'Baixando vídeo...';
      case 'TRANSCRIBING':
        return 'Transcrevendo áudio...';
      case 'PROCESSING':
        return 'Gerando artefatos...';
      case 'PROCESSED':
      case 'COMPLETED':
        return 'Processamento concluído!';
      case 'ERROR':
        return 'Erro no processamento';
      default:
        return 'Processando...';
    }
  };

  const getStatusIcon = () => {
    if (!status) {
      return <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
    }
    
    switch (status.status) {
      case 'DOWNLOADING':
        return <Download className="w-5 h-5 text-blue-500 animate-bounce" />;
      case 'TRANSCRIBING':
        return <Zap className="w-5 h-5 text-purple-500 animate-pulse" />;
      case 'PROCESSING':
        return <Zap className="w-5 h-5 text-orange-500 animate-pulse" />;
      case 'COMPLETED':
      case 'PROCESSED':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'ERROR':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />;
    }
  };

  const getStatusColor = () => {
    if (!status) return 'border-blue-300 bg-blue-50';
    
    switch (status.status) {
      case 'DOWNLOADING':
        return 'border-blue-300 bg-blue-50';
      case 'TRANSCRIBING':
      case 'PROCESSING':
        return 'border-purple-300 bg-purple-50';
      case 'COMPLETED':
      case 'PROCESSED':
        return 'border-green-300 bg-green-50';
      case 'ERROR':
        return 'border-red-300 bg-red-50';
      default:
        return 'border-gray-300 bg-gray-50';
    }
  };

  const showLongVideoWarning = elapsedTime > 30 && 
    status && 
    status.status !== 'COMPLETED' && 
    status.status !== 'PROCESSED';

  const shouldAutoClose = status && (status.status === 'COMPLETED' || status.status === 'PROCESSED' || status.status === 'ERROR');

  return (
    <Card className={`fixed bottom-4 right-4 w-96 border-2 ${getStatusColor()} shadow-lg z-50`}>
      <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <CardTitle className="text-base">{getStatusMessage()}</CardTitle>
        </div>
        {shouldAutoClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="h-auto w-auto p-1"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Progresso:</span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {elapsedTime}s
            </span>
          </div>
          <Progress value={getProgressPercent()} className="h-2" />
        </div>

        {/* Status Details */}
        {status && status.status && (
          <div className="text-sm text-gray-600 space-y-1">
            {status.progress?.step && (
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-blue-400 rounded-full"></span>
                <span>{status.progress.step}</span>
              </div>
            )}
            
            {/* Timestamps */}
            {status.downloaded_at && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Baixado: {new Date(status.downloaded_at).toLocaleTimeString('pt-BR')}</span>
              </div>
            )}
            {status.transcribed_at && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                <span>Transcrito: {new Date(status.transcribed_at).toLocaleTimeString('pt-BR')}</span>
              </div>
            )}
          </div>
        )}

        {/* Processando - Sempre mostra se houver artefatos */}
        {processingArtifacts && processingArtifacts.length > 0 && (
          <div className="text-sm bg-orange-50 border border-orange-200 rounded p-3">
            <div className="flex items-center gap-2 font-medium text-orange-700">
              <span className="inline-block w-2 h-2 bg-orange-400 rounded-full animate-pulse"></span>
              <span>Gerando artefatos</span>
            </div>
            <div className="text-xs text-orange-600 mt-1">
              {processingArtifacts.length === 1 ? (
                <span>{processingArtifacts[0]}</span>
              ) : (
                <span>{processingArtifacts[0]} + {processingArtifacts.length - 1} na fila</span>
              )}
            </div>
          </div>
        )}

        {/* Long Video Warning */}
        {showLongVideoWarning && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
            <p className="font-medium">⏱️ Vídeo muito longo</p>
            <p className="text-xs mt-1">
              Este vídeo está levando mais tempo do que o normal para processar.
            </p>
          </div>
        )}

        {/* Error Message */}
        {status && status.status === 'ERROR' && status.error_message && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
            <p className="font-medium">❌ Erro</p>
            <p className="text-xs mt-1">{status.error_message}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FloatingProcessingIndicator;
